import {Group, GroupProps, packSx, Stack, StackProps, Text} from '@mantine/core';
import React, {PropsWithChildren} from 'react';

import {FONT_WEIGHT} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

export type SectionRowProps = PropsWithChildren<GroupProps>;

/**
 * A row within a {@link BenchlingNodeV2.Section}. This is a flex row with BenchlingNode styling applied.
 *
 * @package
 */
export function SectionRow(props: SectionRowProps): JSX.Element {
  return <Group px="sm" pos="relative" {...props} />;
}

export interface SectionProps {
  /**
   * The name of the section. This is displayed in small, black, semibold text at the top of the section.
   */
  name: string;
}

/**
 * A section within a {@link BenchlingNodeV2.Sections}. This is a flex column with BenchlingNode styling
 * applied.
 *
 * This component is a context consumer. As such, it should be rendered as a child of a
 * {@link BenchlingNodeV2}.
 *
 * @package
 */
export function Section(props: SectionProps & PropsWithChildren<StackProps>): JSX.Element {
  const {name, children, sx, ...stackProps} = props;

  return (
    <Stack
      spacing="2px"
      sx={[
        (theme) => ({
          '&:nth-child(n)': {
            borderTop: `1px solid ${theme.colors.gray[3]}`,
          },
          '&:first-child': {
            borderTopWidth: '0px',
          },
        }),
        ...packSx(sx),
      ]}
      {...stackProps}
    >
      <Text c="gray.9" fz="xs" fw={FONT_WEIGHT.SEMIBOLD} mt="xs" px="sm" py={0}>
        {name}
      </Text>
      {children}
    </Stack>
  );
}

export type SectionsProps = PropsWithChildren<StackProps>;

/**
 * A wrapper for one or more {@link BenchlingNodeV2.Section}.
 *
 * @package
 */
export function Sections(props: SectionsProps): JSX.Element {
  const {children, ...stackProps} = props;
  return (
    <Stack spacing="xs" {...stackProps}>
      {props.children}
    </Stack>
  );
}
